import React, { Component } from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { withTranslation } from 'react-i18next';

// Tour steps configuration - order: Consult Settings → Kiosk Pagination → Help on Demand
var getTourSteps = function(t) {
  return [
    {
      target: '[data-tour="consult-settings"]',
      title: t ? t('Tour.ConsultSettingsTitle') : 'Consult Settings',
      content: t ? t('Tour.ConsultSettingsContent') : 'Configure your audio devices and consultation preferences for video and voice consultations with patients.',
      placement: 'bottom',
      disableBeacon: true,
      isFixed: true,
    },
    {
      target: '[data-tour="kiosk-pagination"]',
      title: t ? t('Tour.PaginationTitle') : 'Enhanced Kiosk Pagination',
      content: t ? t('Tour.PaginationContent') : 'Navigate easily through the kiosk extensions with enhanced pagination controls.',
      placement: 'top',
      disableBeacon: true,
      isFixed: true,
    },
    {
      target: '[data-tour="help-tours-menu"]',
      title: t ? t('Tour.HelpTitle') : 'Help on Demand',
      content: t ? t('Tour.HelpContent') : 'Find these tutorials and more for a refresher anytime.',
      placement: 'right',
      disableBeacon: true,
      isFixed: true,
    },
  ];
};

// Brand color for tour UI (matches joyride primaryColor)
var TOUR_BRAND_COLOR = '#00a7e1';
var TOUR_BRAND_GRADIENT = 'linear-gradient(135deg, #00a7e1 0%, #0077a3 100%)';

// LocalStorage utilities for tour management
var TOUR_STORAGE_KEY = 'rxconnect_tour_completed';

var getAuthToken = function() {
  try {
    var u = JSON.parse(sessionStorage.getItem('user'));
    return (u && u.signInUserSession && u.signInUserSession.idToken && u.signInUserSession.idToken.jwtToken) || '';
  } catch (e) {
    return '';
  }
};

var saveTourWelcomeHiddenPreference = function(userId, onSuccess) {
  var token = getAuthToken();
  fetch(process.env.REACT_APP_BASEURL + 'user/tour-welcome-hidden', {
    method: 'PATCH',
    headers: {
      Authorization: 'Bearer ' + token,
      'Content-Type': 'application/json',
    },
  })
    .then(function(res) { return res.ok ? res.json() : Promise.reject(new Error('Failed to save preference')); })
    .then(function(data) {
      if (data && data.success && onSuccess) onSuccess();
    })
    .catch(function(err) {
      console.error('[TourGuide] Failed to save tour welcome hidden preference', err);
      setTourCompleted(userId);
    });
};

var getTourCompletionStatus = function(userId) {
  try {
    var storedData = localStorage.getItem(TOUR_STORAGE_KEY);
    if (storedData) {
      var completedUsers = JSON.parse(storedData);
      return completedUsers.includes(userId);
    }
    return false;
  } catch (error) {
    console.error('[TourGuide] Error reading tour completion status:', error);
    return false;
  }
};

var setTourCompleted = function(userId) {
  try {
    var storedData = localStorage.getItem(TOUR_STORAGE_KEY);
    var completedUsers = storedData ? JSON.parse(storedData) : [];
    if (!completedUsers.includes(userId)) {
      completedUsers.push(userId);
      localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(completedUsers));
      console.log('[TourGuide] Tour completion saved for user:', userId);
    }
  } catch (error) {
    console.error('[TourGuide] Error saving tour completion status:', error);
  }
};

// Joyride styles configuration
var joyrideStyles = {
  options: {
    primaryColor: TOUR_BRAND_COLOR,
    zIndex: 10000,
    arrowColor: '#fff',
    backgroundColor: '#fff',
    overlayColor: 'rgba(0, 0, 0, 0.5)',
    textColor: '#333',
  },
  tooltip: {
    borderRadius: 12,
    fontSize: 14,
  },
  tooltipContainer: {
    textAlign: 'left',
  },
  tooltipTitle: {
    fontSize: 16,
    fontWeight: 600,
  },
  tooltipContent: {
    padding: '10px 0',
  },
  buttonNext: {
    backgroundColor: TOUR_BRAND_COLOR,
    borderRadius: 6,
    fontSize: 14,
  },
  buttonBack: {
    color: '#666',
    marginRight: 10,
  },
  buttonSkip: {
    color: '#999',
  },
  spotlight: {
    borderRadius: 8,
  },
};

// Wrapped component that can accept refs
var TourGuideComponent = class extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showWelcomeModal: false,
      runTour: false,
      stepIndex: 0,
      pendingNavigation: null,
      dontAskAgain: false,
    };
    this.handleJoyrideCallback = this.handleJoyrideCallback.bind(this);
    this.handleStartTour = this.handleStartTour.bind(this);
    this.handleSkipTour = this.handleSkipTour.bind(this);
    this.checkFirstLogin = this.checkFirstLogin.bind(this);
    this.startTourManually = this.startTourManually.bind(this);
  }

  componentDidMount() {
    // Expose instance to parent via ref callback if provided
    if (this.props.onRef) {
      this.props.onRef(this);
    }
    this.checkFirstLogin();
  }

  componentDidUpdate(prevProps, prevState) {
    // Update ref callback if it changed
    if (this.props.onRef && this.props.onRef !== prevProps.onRef) {
      this.props.onRef(this);
    }
    
    if (prevProps.userId !== this.props.userId && this.props.userId) {
      this.checkFirstLogin();
    }
    
    // Close welcome modal if tourWelcomeHidden becomes true
    if (prevProps.tourWelcomeHidden !== this.props.tourWelcomeHidden && this.props.tourWelcomeHidden) {
      this.setState({ showWelcomeModal: false });
    }

    // Lock body scroll when tour runs so backdrop doesn't get pushed by page scroll
    if (prevState.runTour !== this.state.runTour) {
      if (this.state.runTour) {
        this._bodyOverflow = document.body.style.overflow;
        this._htmlOverflow = document.documentElement.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = this._bodyOverflow != null ? this._bodyOverflow : '';
        document.documentElement.style.overflow = this._htmlOverflow != null ? this._htmlOverflow : '';
      }
    }

    // When we reach the last step (Help on Demand), scroll the Help & Tours item into view in the sidebar
    var lastStepIndex = 2; // Consult Settings 0, Kiosk 1, Help on Demand 2
    if (this.state.runTour && this.state.stepIndex === lastStepIndex && prevState.stepIndex !== lastStepIndex) {
      setTimeout(function() {
        var el = document.querySelector('[data-tour="help-tours-menu"]');
        if (el) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }, 150);
    }
  }

  componentWillUnmount() {
    // Clear ref on unmount
    if (this.props.onRef) {
      this.props.onRef(null);
    }
    // Restore body scroll if tour was still running
    if (this.state.runTour) {
      document.body.style.overflow = this._bodyOverflow != null ? this._bodyOverflow : '';
      document.documentElement.style.overflow = this._htmlOverflow != null ? this._htmlOverflow : '';
    }
  }

  checkFirstLogin() {
    var userId = this.props.userId;
    var tourWelcomeHidden = this.props.tourWelcomeHidden;
    var self = this;

    if (tourWelcomeHidden || !userId || getTourCompletionStatus(userId)) {
      return;
    }

    setTimeout(function() {
      if (self.props.tourWelcomeHidden) return;
      self.setState({ showWelcomeModal: true });
    }, 1500);
  }

  // Helper function to wait for an element to appear in the DOM
  waitForElement(selector, timeout, callback) {
    var startTime = Date.now();
    var checkInterval = 100; // Check every 100ms
    
    var checkElement = function() {
      var element = document.querySelector(selector);
      if (element) {
        console.log('[TourGuide] Target element found:', selector, {
          element: element,
          visible: element.offsetParent !== null,
          elapsed: Date.now() - startTime + 'ms'
        });
        callback(true, element);
      } else if (Date.now() - startTime < timeout) {
        setTimeout(checkElement, checkInterval);
      } else {
        console.warn('[TourGuide] Target element not found after timeout:', selector, {
          timeout: timeout + 'ms',
          elapsed: Date.now() - startTime + 'ms'
        });
        callback(false, null);
      }
    };
    
    checkElement();
  }

  handleStartTour() {
    var self = this;
    var navigate = this.props.navigate;
    console.log('[TourGuide] handleStartTour called - closing modal and starting tour', {
      hasNavigate: !!navigate,
      currentPath: window.location.hash || window.location.pathname
    });
    
    // Close welcome modal first
    this.setState({
      showWelcomeModal: false,
    }, function() {
      console.log('[TourGuide] Welcome modal closed');
      
      // Check if we need to navigate to Deliver Prescription page for pagination step
      var currentPath = (window.location.hash || window.location.pathname).toLowerCase();
      var needsNavigation = !currentPath.includes('/prescription/assign') && !currentPath.includes('/home/prescription/assign');
      
      if (needsNavigation && navigate) {
        console.log('[TourGuide] Navigating to Deliver Prescription page for pagination tour step');
        // Navigate to Deliver Prescription page
        navigate('/home/prescription/assign');
        
        // Wait for navigation, then wait for the pagination element to appear
        setTimeout(function() {
          console.log('[TourGuide] Page navigated, waiting for pagination element to appear');
          // Wait up to 5 seconds for the pagination element to appear
          self.waitForElement('[data-tour="kiosk-pagination"]', 5000, function(found, element) {
            if (found) {
              console.log('[TourGuide] Pagination element found, starting tour in 300ms');
              setTimeout(function() {
                console.log('[TourGuide] Starting tour now');
                self.setState({
                  runTour: true,
                  stepIndex: 0,
                });
              }, 300);
            } else {
              console.warn('[TourGuide] Pagination element not found, starting tour anyway (may skip first step)');
              // Start tour anyway - react-joyride will handle TARGET_NOT_FOUND
              setTimeout(function() {
                console.log('[TourGuide] Starting tour now (without pagination element)');
                self.setState({
                  runTour: true,
                  stepIndex: 0,
                });
              }, 300);
            }
          });
        }, 1000); // Increased initial delay to 1 second for page load
      } else {
        console.log('[TourGuide] Already on correct page or no navigate function, waiting for pagination element');
        // Wait for the pagination element to appear before starting
        self.waitForElement('[data-tour="kiosk-pagination"]', 3000, function(found, element) {
          if (found) {
            console.log('[TourGuide] Pagination element found, starting tour in 300ms');
            setTimeout(function() {
              console.log('[TourGuide] Starting tour now');
              self.setState({
                runTour: true,
                stepIndex: 0,
              });
            }, 300);
          } else {
            console.warn('[TourGuide] Pagination element not found, starting tour anyway');
            setTimeout(function() {
              console.log('[TourGuide] Starting tour now (without pagination element)');
              self.setState({
                runTour: true,
                stepIndex: 0,
              });
            }, 300);
          }
        });
      }
    });
  }

  handleSkipTour() {
    var dontAskAgain = this.state.dontAskAgain;
    var userId = this.props.userId;
    var onTourWelcomeHiddenSaved = this.props.onTourWelcomeHiddenSaved;

    this.setState({ showWelcomeModal: false, dontAskAgain: false });

    if (dontAskAgain && userId) {
      saveTourWelcomeHiddenPreference(userId, onTourWelcomeHiddenSaved);
    }
  }

  handleJoyrideCallback(data) {
    var action = data.action;
    var index = data.index;
    var status = data.status;
    var type = data.type;
    var userId = this.props.userId;

    if (action === ACTIONS.CLOSE) {
      this.setState({ 
        runTour: false, 
        stepIndex: 0 
      });
      return; // Exit early, don't process further
    }

    if ([STATUS.SKIPPED].includes(status)) {
      this.setState({ 
        runTour: false, 
        stepIndex: 0 
      });
      return; // Exit early, don't process further
    }

    // Handle step navigation (only if not closing, not skipped, and not finished)
    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type) && 
        action !== ACTIONS.CLOSE && 
        status !== STATUS.SKIPPED && 
        status !== STATUS.FINISHED) {
      // Update step index
      var nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      console.log('[TourGuide] Moving to step index:', nextIndex, {
        eventType: type,
        action: action,
        currentIndex: index,
        target: data.step && data.step.target ? data.step.target : 'N/A'
      });
      
      // If target not found, log warning
      if (type === EVENTS.TARGET_NOT_FOUND) {
        console.warn('[TourGuide] Target element not found for step:', {
          stepIndex: index,
          target: data.step && data.step.target ? data.step.target : 'N/A',
          title: data.step && data.step.title ? data.step.title : 'N/A'
        });
      }
      
      this.setState({ stepIndex: nextIndex });
    }

    // Handle tour finished status
    if ([STATUS.FINISHED].includes(status)) {
      // Tour completed - save to localStorage
      console.log('[TourGuide] Tour finished - saving completion status');
      setTourCompleted(userId);
      this.setState({ 
        runTour: false, 
        stepIndex: 0 
      });
    }
  }

  // Public method to restart tour from Help & Tours menu
  startTourManually() {
    var self = this;
    var navigate = this.props.navigate;
    console.log('[TourGuide] startTourManually called from Help & Tours menu', {
      hasNavigate: !!navigate,
      currentPath: window.location.hash || window.location.pathname
    });
    console.log('[TourGuide] Current state:', {
      runTour: this.state.runTour,
      stepIndex: this.state.stepIndex,
      showWelcomeModal: this.state.showWelcomeModal
    });
    
    // Check if we need to navigate to Deliver Prescription page
    var currentPath = (window.location.hash || window.location.pathname).toLowerCase();
    var needsNavigation = !currentPath.includes('/prescription/assign') && !currentPath.includes('/home/prescription/assign');
    
    if (needsNavigation && navigate) {
      console.log('[TourGuide] Navigating to Deliver Prescription page for pagination tour step');
      navigate('/home/prescription/assign');
      
      // Wait for navigation, then wait for the pagination element to appear
      setTimeout(function() {
        console.log('[TourGuide] Page navigated, waiting for pagination element to appear');
        // Wait up to 5 seconds for the pagination element to appear
        self.waitForElement('[data-tour="kiosk-pagination"]', 5000, function(found, element) {
          if (found) {
            console.log('[TourGuide] Pagination element found, starting tour in 300ms');
            setTimeout(function() {
              console.log('[TourGuide] Setting runTour to true');
              self.setState({
                runTour: true,
                stepIndex: 0,
              });
            }, 300);
          } else {
            console.warn('[TourGuide] Pagination element not found, starting tour anyway');
            setTimeout(function() {
              console.log('[TourGuide] Setting runTour to true (without pagination element)');
              self.setState({
                runTour: true,
                stepIndex: 0,
              });
            }, 300);
          }
        });
      }, 1000); // Increased initial delay to 1 second for page load
    } else {
      // Ensure any previous tour state is cleared, then wait for element
      this.setState({
        runTour: false,
        stepIndex: 0,
      }, function() {
        console.log('[TourGuide] State cleared, waiting for pagination element');
        // Wait for the pagination element to appear before starting
        self.waitForElement('[data-tour="kiosk-pagination"]', 3000, function(found, element) {
          if (found) {
            console.log('[TourGuide] Pagination element found, starting tour in 300ms');
            setTimeout(function() {
              console.log('[TourGuide] Setting runTour to true');
              self.setState({
                runTour: true,
                stepIndex: 0,
              });
            }, 300);
          } else {
            console.warn('[TourGuide] Pagination element not found, starting tour anyway');
            setTimeout(function() {
              console.log('[TourGuide] Setting runTour to true (without pagination element)');
              self.setState({
                runTour: true,
                stepIndex: 0,
              });
            }, 300);
          }
        });
      });
    }
  }

  render() {
    var showWelcomeModal = this.state.showWelcomeModal;
    var runTour = this.state.runTour;
    var stepIndex = this.state.stepIndex;
    var t = this.props.t;
    var steps = getTourSteps(t);

    return (
      <React.Fragment>
        {/* Welcome Modal - Lower z-index initially to appear below consultation modal */}
        <Dialog
          open={showWelcomeModal}
          onClose={this.handleSkipTour}
          maxWidth="sm"
          fullWidth
          style={{
            zIndex: 1200, // Lower than consultation modal (1300) initially
          }}
          PaperProps={{
            style: {
              borderRadius: 16,
              overflow: 'hidden',
              zIndex: 1200,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            },
          }}
          BackdropProps={{
            style: {
              zIndex: 1199,
            },
          }}
        >
          <Box
            style={{
              background: 'linear-gradient(135deg, #00a7e1 0%, #0077a3 100%)',
              color: 'white',
              position: 'relative',
              padding: '32px 24px',
              textAlign: 'center',
            }}
          >
            <IconButton
              onClick={this.handleSkipTour}
              style={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'white',
              }}
            >
              <CloseIcon />
            </IconButton>
            <LocationOnIcon 
              style={{ 
                fontSize: 64, 
                marginBottom: 16,
                color: 'white'
              }} 
            />
            <Typography variant="h4" style={{ fontWeight: 'bold', marginBottom: 8 }}>
              {t ? t('Tour.WelcomeTitle') : 'Welcome to iLocalBox!'}
            </Typography>
            <Typography variant="body1" style={{ opacity: 0.9 }}>
              {t ? t('Tour.WelcomeSubtitle') : "Let's take a quick tour to help you get started"}
            </Typography>
          </Box>
          <DialogContent style={{ padding: '32px 24px', textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary" style={{ marginBottom: 24 }}>
              {t ? t('Tour.WelcomeDescription') : "Discover the new features and enhancements we've added to make your experience better."}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t ? t('Tour.WelcomeDuration') : 'This tour takes about 30 seconds'}
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              py: 2.5,
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              borderTop: '1px solid',
              borderColor: 'rgba(0, 167, 225, 0.12)',
              backgroundColor: 'rgba(0, 167, 225, 0.03)',
              mt: 0,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={this.state.dontAskAgain}
                  onChange={(e) => this.setState({ dontAskAgain: e.target.checked })}
                  size="small"
                  sx={{
                    padding: '4px',
                    color: 'rgba(0, 167, 225, 0.5)',
                    '&.Mui-checked': {
                      color: '#00a7e1',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: 20,
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                    },
                    '&:hover': {
                      color: 'rgba(0, 167, 225, 0.8)',
                      backgroundColor: 'rgba(0, 167, 225, 0.06)',
                      borderRadius: '4px',
                    },
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(0, 0, 0, 0.6)',
                    fontSize: '15px',
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                  }}
                >
                  {t ? t('Tour.DontAskAgain') : "Don't ask me again"}
                </Typography>
              }
              sx={{
                margin: 0,
                userSelect: 'none',
                padding: '6px 12px',
                marginRight: '-12px',
                marginLeft: '-12px',
                borderRadius: '8px',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(0, 167, 225, 0.04)',
                },
              }}
            />
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                onClick={this.handleSkipTour}
                startIcon={<SkipNextIcon sx={{ fontSize: 18 }} />}
                sx={{
                  color: 'text.secondary',
                  textTransform: 'none',
                  fontSize: '15px',
                }}
              >
                {t ? t('Tour.SkipButton') : 'Skip for now'}
              </Button>
              <Button
                onClick={this.handleStartTour}
                variant="contained"
                startIcon={<PlayArrowIcon sx={{ fontSize: 18 }} />}
                sx={{
                  background: TOUR_BRAND_GRADIENT,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '15px',
                  boxShadow: '0 2px 8px rgba(0, 167, 225, 0.35)',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 167, 225, 0.4)',
                  },
                }}
              >
                {t ? t('Tour.StartButton') : 'Start Tour'}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Joyride Tour */}
        <Joyride
          steps={steps}
          run={runTour}
          stepIndex={stepIndex}
          continuous={true}
          showProgress={true}
          showSkipButton={true}
          scrollToFirstStep={true}
          disableOverlayClose={true}
          disableScrolling={true}
          callback={this.handleJoyrideCallback}
          styles={joyrideStyles}
          floaterProps={{
            disableAnimation: true,
          }}
          locale={{
            back: t ? t('Tour.Back') : 'Back',
            close: t ? t('Tour.Close') : 'Close',
            last: t ? t('Tour.Finish') : 'Finish',
            next: t ? t('Tour.Next') : 'Next',
            skip: t ? t('Tour.Skip') : 'Skip',
          }}
        />
      </React.Fragment>
    );
  }
};

// Export the component wrapped with translation
// Note: We'll use a callback ref pattern instead of forwardRef since withTranslation HOC doesn't support refs
var TourGuideWithTranslation = withTranslation()(TourGuideComponent);

export default TourGuideWithTranslation;
export { getTourCompletionStatus, setTourCompleted };



// WEBPACK FOOTER //
// ./src/components/tour/TourGuide.js